import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";

type Metadata = Record<string, string | number | null | undefined>;

type MetadataValidation = {
  isValid: boolean;
  missingFields: string[];
};

const TEMPLATE_FILE_NAME = "Deal Data Input Guidance Template (1).xlsx";
const TEMPLATE_FILE_PATH = `/${encodeURIComponent(TEMPLATE_FILE_NAME)}`;
const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");

const VALID_XLSX_MIME_TYPES = new Set([
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/octet-stream",
]);

const formatNumberToOneDecimal = (value: unknown): string => {
  const trimmedValue = String(value || "").trim();
  if (trimmedValue === "") return "";
  const numValue = Number(trimmedValue);
  return Number.isFinite(numValue) ? String(numValue.toFixed(1)) : trimmedValue;
};

const buildMetadataPayload = (formData: Metadata) => ({
  project_name: String(formData["Project Name (Target)"] || "").trim(),
  company_revenue: formatNumberToOneDecimal(formData["Company Revenue"]),
  ebitda: formatNumberToOneDecimal(formData["EBITDA"]),
  number_of_employees: String(formData["Number of Employees"] || "").trim(),
  industry: String(formData["Industry"] || "").trim(),
  sector: String(formData["Sector"] || "").trim(),
  subsector: String(formData["Subsector"] || "").trim(),
  census_date: String(formData["Census Date"] || "").trim(),
  ltm_date: String(formData["LTM Date"] || "").trim(),
  geography: String(formData["Geography"] || "").trim(),
  ownership_type: String(formData["Ownership Type"] || "").trim(),
  account_aligned: String(formData["Account Aligned (PE Firm)"] || "").trim(),
});

const extractCompanyIdFromUploadResponse = (payload: any): number | null => {
  const candidates = [
    payload?.company_id,
    payload?.companyId,
    payload?.data?.company_id,
    payload?.data?.companyId,
    payload?.result?.company_id,
    payload?.result?.companyId,
  ];

  for (const candidate of candidates) {
    const parsed = Number(candidate);
    if (Number.isInteger(parsed) && parsed > 0) {
      return parsed;
    }
  }

  return null;
};

const extractCompanyNameFromUploadResponse = (payload: any): string | null => {
  const candidates = [
    payload?.company_name,
    payload?.companyName,
    payload?.data?.company_name,
    payload?.data?.companyName,
    payload?.result?.company_name,
    payload?.result?.companyName,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === "string" && candidate.trim() !== "") {
      return candidate.trim();
    }
  }

  return null;
};

const validateMetadata = (metadata: Metadata): MetadataValidation => {
  const fieldsToValidate = [
    "Company Revenue",
    "EBITDA",
    "Number of Employees",
    "Industry",
    "Sector",
    "Subsector",
    "Geography",
    "Ownership Type",
    "Project Name (Target)",
    "Account Aligned (PE Firm)",
  ];

  const missingFields: string[] = [];
  for (const field of fieldsToValidate) {
    const value = metadata?.[field];
    const isValueEmpty = value === null || value === undefined || String(value).trim() === "";
    if (isValueEmpty) {
      missingFields.push(field);
    }
  }

  return {
    isValid: missingFields.length === 0,
    missingFields,
  };
};

type CompareCompanyProps = {
  onClick?: () => void;
};

const CompareCompany = (_props: CompareCompanyProps) => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [metaData, setMetaData] = useState<Metadata | null>(null);
  const [metadataValidation, setMetadataValidation] = useState<MetadataValidation | null>(null);
  const [fileTypeError, setFileTypeError] = useState("");
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const previewUploadedFile = async (file: File) => {
    setUploadedFile(file);
    localStorage.setItem("uploadedTempFileName", String(file?.name || ""));
    setMetadataValidation(null);
    setIsLoadingMetadata(true);
    try {
      const form = new FormData();
      form.append("file", file);

      const response = await fetch(`${API_BASE_URL}/api/company-metadata/preview`, {
        method: "POST",
        body: form,
      });

      if (!response.ok) {
        const text = await response.text();
        throw new Error(text || "Failed to preview uploaded file");
      }

      const result = await response.json();
      const metadata = (result?.metadata || {}) as Metadata;
      setMetaData(metadata);

      const validation = validateMetadata(metadata);
      setMetadataValidation(validation);
    } catch (error: any) {
      console.error("Metadata preview failed", error);
      alert("Unable to fetch metadata preview: " + (error?.message || error));
    } finally {
      setIsLoadingMetadata(false);
    }
  };

  const handleSubmit = async (): Promise<boolean> => {
    if (!uploadedFile) {
      alert("Please upload a standardized file before proceeding.");
      return false;
    }

    if (!metaData) {
      alert("Please wait for metadata to load before submitting.");
      return false;
    }

    const validation = validateMetadata(metaData);
    if (!validation.isValid) {
      alert(`The following required metadata fields are missing or empty:\n\n${validation.missingFields.join("\n")}\n\nPlease update your file and try again.`);
      return false;
    }

    const metadataPayload = buildMetadataPayload(metaData);
    if (!metadataPayload.project_name) {
      alert("Project Name is required in metadata before submission.");
      return false;
    }

    const form = new FormData();
    form.append("file", uploadedFile);
    form.append("metadata", JSON.stringify(metadataPayload));

    setIsSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/temp/upload`, {
        method: "POST",
        body: form,
      });

      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || "Upload failed");
      }

      const data = await res.json();
      const companyId = extractCompanyIdFromUploadResponse(data);
      const companyName = extractCompanyNameFromUploadResponse(data);

      if (companyId !== null) {
        localStorage.setItem("uploadedTempCompanyId", String(companyId));
      }

      if (companyName) {
        localStorage.setItem("uploadedTempCompanyName", companyName);
      }

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setUploadedFile(null);
      setMetaData(null);
      setMetadataValidation(null);
      return true;
    } catch (error: any) {
      console.error("Upload error", error);
      alert("Upload failed: " + (error?.message || error));
      return false;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownloadTemplate = () => {
    const link = document.createElement("a");
    link.href = TEMPLATE_FILE_PATH;
    link.download = TEMPLATE_FILE_NAME;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleRemoveUploadedFile = () => {
    setUploadedFile(null);
    setMetaData(null);
    setMetadataValidation(null);
    setFileTypeError("");
    setIsLoadingMetadata(false);
    localStorage.removeItem("uploadedTempFileName");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const isValidXlsxFile = (file: File | null): boolean => {
    if (!file) return false;
    const fileName = String(file.name || "").toLowerCase();
    const hasXlsxExtension = fileName.endsWith(".xlsx");
    const hasValidMime = file.type ? VALID_XLSX_MIME_TYPES.has(file.type) : false;
    return hasXlsxExtension || hasValidMime;
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (uploadedFile) return;

    const file = event.target.files?.[0] || null;
    if (!file) return;

    if (!isValidXlsxFile(file)) {
      setFileTypeError("Only .xlsx files are allowed.");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      return;
    }

    previewUploadedFile(file);
  };

  return (
    <div className={`${isDarkMode ? "bg-black" : "bg-[#f4fbf6]"} min-h-screen w-full relative overflow-hidden`}>
      {!isDarkMode && (
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-16 -left-10 h-56 w-56 rounded-full bg-emerald-100/70 blur-3xl"></div>
          <div className="absolute top-20 right-10 h-48 w-48 rounded-full bg-sky-100/70 blur-3xl"></div>
          <div className="absolute bottom-8 left-1/3 h-44 w-44 rounded-full bg-indigo-100/60 blur-3xl"></div>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-0 pb-10 overflow-visible">
        <div className="text-center pt-8 pb-8">
          <span className="inline-flex items-center rounded-full border border-emerald-300/80 bg-emerald-50 px-4 py-1 text-xs font-bold uppercase tracking-wide text-emerald-800 dark:border-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-200">
            Company Comparison Setup
          </span>
          <h2 className="mt-3 text-2xl md:text-3xl font-extrabold text-gray-900 dark:text-white mb-3">Compare a Company</h2>
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-300">Please standardize and upload your company file before comparison</p>
        </div>

        <div className="max-w-7xl mx-auto px-6 pb-12">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4">
            <div className="min-w-[260px] max-w-sm w-full bg-white/95 dark:bg-gray-900/95 border border-emerald-100 dark:border-emerald-800/60 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Step 1: Download Template</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Use the standardization template and field instructions before upload</p>
              <button onClick={handleDownloadTemplate} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-300/40 transition-all">
                Download Template
              </button>
            </div>

            <span aria-hidden="true" className="text-2xl font-bold text-gray-500 dark:text-gray-300">
              <span className="md:hidden">↓</span>
              <span className="hidden md:inline">→</span>
            </span>

            <div className="min-w-[260px] max-w-sm w-full bg-white/95 dark:bg-gray-900/95 border border-sky-100 dark:border-sky-800/60 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Step 2: Standardize Company Data</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">Populate the template with company financial and KPI data in the required format</p>
            </div>

            <span aria-hidden="true" className="text-2xl font-bold text-gray-500 dark:text-gray-300">
              <span className="md:hidden">↓</span>
              <span className="hidden md:inline">→</span>
            </span>

            <div className="min-w-[260px] max-w-sm w-full bg-white/95 dark:bg-gray-900/95 border border-indigo-100 dark:border-indigo-800/60 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur p-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Step 3: Upload Standardized File</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">Upload the standardized company file and click Compare</p>
              <input ref={fileInputRef} type="file" accept=".xlsx" className="hidden" onChange={handleFileChange} />
              <button onClick={() => fileInputRef.current?.click()} className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm bg-indigo-700 text-white hover:bg-indigo-800 hover:shadow-lg hover:shadow-indigo-300/40 transition-all">
                Upload Standardized File
              </button>
              {fileTypeError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{fileTypeError}</p>}
              {uploadedFile && (
                <div className="mt-4 flex items-center justify-between gap-3">
                  <p className="text-sm text-gray-700 dark:text-gray-300">Selected file: <span className="font-medium truncate" title={uploadedFile.name}>{uploadedFile.name}</span></p>
                  <button type="button" onClick={handleRemoveUploadedFile} className="text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 whitespace-nowrap">Remove file</button>
                </div>
              )}
            </div>
          </div>
        </div>

        {uploadedFile && (
          <div className="flex justify-center pt-6">
            {isLoadingMetadata ? (
              <div className="bg-white/95 dark:bg-gray-900/95 border border-emerald-100 dark:border-emerald-800/60 p-5 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur text-center w-full max-w-xl">
                <p className="text-sm font-medium text-green-700 dark:text-green-300">Loading Data...</p>
              </div>
            ) : (
              <div tabIndex={0} className="bg-white/95 dark:bg-gray-900/95 border border-emerald-100 dark:border-emerald-800/60 p-5 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur transition-all duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 text-center w-full max-w-xl">
                <h2 className="text-xl font-bold text-green-900 dark:text-green-400 mt-0 mb-2">Standardized File Ready</h2>
                <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">File name: {uploadedFile.name} | Size: {(uploadedFile.size / 1024).toFixed(1)} KB | Status: Ready for comparison</p>
              </div>
            )}
          </div>
        )}

        {metaData && metadataValidation && (
          <div className="flex justify-center pt-6">
            <div className="w-full max-w-2xl bg-white/95 dark:bg-gray-900/95 border border-indigo-100 dark:border-indigo-800/60 rounded-2xl shadow-[0_14px_40px_rgba(16,24,40,0.08)] backdrop-blur p-6">
              <h3 className="text-lg font-bold mb-4 text-indigo-900 dark:text-indigo-200">Data Validation</h3>
              {!metadataValidation.isValid && (
                <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded p-4 mb-4">
                  <p className="text-red-800 dark:text-red-200 font-medium mb-2">✗ Missing required data fields:</p>
                  <ul className="list-disc list-inside text-red-700 dark:text-red-300 text-sm">
                    {metadataValidation.missingFields.map((field) => (
                      <li key={field}>{field}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-center pt-6">
          <button
            onClick={async () => {
              setIsSubmitting(true);
              try {
                const isSubmitted = await handleSubmit();
                if (isSubmitted) {
                  navigate("/benchmark");
                }
              } finally {
                setIsSubmitting(false);
              }
            }}
            disabled={!uploadedFile || !metadataValidation || !metadataValidation.isValid}
            className={`${!uploadedFile || !metadataValidation || !metadataValidation.isValid ? "bg-gray-400 text-gray-600 cursor-not-allowed" : "bg-emerald-700 text-white hover:bg-emerald-800 hover:shadow-lg hover:shadow-emerald-300/40"} px-7 py-2.5 rounded-xl font-semibold text-sm transition-all`}
          >
            {isSubmitting ? "Submitting..." : "Continue"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CompareCompany;